import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';

export interface Client {
  id: string;
  first_name: string;
  last_name: string;
  /** Computed: first_name + ' ' + last_name — kept for backward compatibility with code using client.name */
  name: string;
  email: string | null;
  phone: string | null;
  birth_date: string | null;
  address: Record<string, string>;
  notes: string;
  photo_url: string | null;
  emergency_contact: Record<string, string>;
  style_preferences: string[];
  referral_source: string | null;
  instagram: string | null;
  is_vip: boolean;
  archived: boolean;
  created_at: string;
  updated_at: string;
  contract_count: number;
}

export function useClients() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['clients', user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Client[]> => {
      const { data: clients, error } = await (supabase as any)
        .from('clients')
        .select('*')
        .eq('owner_id', user!.id)
        .eq('archived', false)
        .order('created_at', { ascending: false });
      if (error) throw error;

      const { data: contracts, error: cErr } = await supabase
        .from('signed_contracts')
        .select('client_id')
        .eq('owner_id', user!.id)
        .not('client_id', 'is', null);
      if (cErr) throw cErr;

      const countMap: Record<string, number> = {};
      (contracts || []).forEach((c: any) => {
        countMap[c.client_id] = (countMap[c.client_id] || 0) + 1;
      });

      return (clients || []).map((c: any) => {
        const first = c.first_name || '';
        const last  = c.last_name  || '';
        const full  = `${first} ${last}`.trim() || c.name || '';
        return {
          id: c.id,
          first_name: first,
          last_name:  last,
          name:       full,
          email:      c.email,
          phone:      c.phone,
          birth_date: c.birth_date,
          address:    (c.address as Record<string, string>) || {},
          notes:      c.notes || '',
          photo_url:  c.photo_url || null,
          emergency_contact: (c.emergency_contact as Record<string, string>) || {},
          style_preferences: (c.style_preferences as string[]) || [],
          referral_source: c.referral_source || null,
          instagram:  c.instagram || null,
          is_vip:     c.is_vip || false,
          archived:   c.archived || false,
          created_at: c.created_at,
          updated_at: c.updated_at,
          contract_count: countMap[c.id] || 0,
        };
      });
    },
  });

  const createClient = useMutation({
    mutationFn: async (data: {
      first_name: string; last_name: string;
      email?: string; phone?: string; birth_date?: string; notes?: string;
      address?: Record<string, string>;
      emergency_contact?: Record<string, string>;
      instagram?: string;
      referral_source?: string | null;
      is_vip?: boolean;
      style_preferences?: string[];
      allergies?: string[]; conditions?: string[]; medications?: string;
      is_pregnant?: boolean; skin_reactions?: string; pain_tolerance?: number | null;
    }) => {
      const fullName = `${data.first_name.trim()} ${data.last_name.trim()}`.trim();
      const { data: client, error } = await supabase.from('clients').insert({
        owner_id:   user!.id,
        name:       fullName,
        first_name: data.first_name.trim(),
        last_name:  data.last_name.trim(),
        email:      data.email || null,
        phone:      data.phone || null,
        birth_date: data.birth_date || null,
        notes:      data.notes || '',
        address:    data.address || {},
        emergency_contact: data.emergency_contact || {},
        referral_source:   data.referral_source || null,
        is_vip:     data.is_vip || false,
        instagram:  data.instagram || null,
        style_preferences: data.style_preferences || [],
      } as any).select().single();
      if (error) { console.error('Client creation error:', JSON.stringify(error, null, 2)); throw error; }
      const hasMedical = data.allergies?.length || data.conditions?.length || data.medications || data.is_pregnant || data.skin_reactions || data.pain_tolerance;
      if (hasMedical && client) {
        await supabase.from('client_medical').insert({
          client_id:     client.id,
          owner_id:      user!.id,
          allergies:     data.allergies || [],
          conditions:    data.conditions || [],
          medications:   data.medications || '',
          is_pregnant:   data.is_pregnant || false,
          skin_reactions: data.skin_reactions || '',
          pain_tolerance: data.pain_tolerance || null,
        });
      }
      return client;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });

  const updateClient = useMutation({
    mutationFn: async (data: {
      id: string;
      first_name?: string; last_name?: string;
      email?: string; phone?: string; birth_date?: string; notes?: string;
      instagram?: string;
      referral_source?: string | null; is_vip?: boolean;
      emergency_contact?: Record<string, string>; style_preferences?: string[];
    }) => {
      const { id, ...updates } = data;
      const clean: Record<string, any> = {};
      if (updates.first_name !== undefined) {
        clean.first_name = updates.first_name;
        clean.last_name  = updates.last_name ?? '';
      }
      if (updates.email !== undefined) clean.email = updates.email || null;
      if (updates.phone !== undefined) clean.phone = updates.phone || null;
      if (updates.birth_date !== undefined) clean.birth_date = updates.birth_date || null;
      if (updates.notes !== undefined) clean.notes = updates.notes;
      if (updates.referral_source !== undefined) clean.referral_source = updates.referral_source || null;
      if (updates.is_vip !== undefined) clean.is_vip = updates.is_vip;
      if (updates.emergency_contact !== undefined) clean.emergency_contact = updates.emergency_contact;
      if (updates.instagram !== undefined) clean.instagram = updates.instagram || null;
      if (updates.style_preferences !== undefined) clean.style_preferences = updates.style_preferences;

      const { error } = await supabase.from('clients').update(clean as any).eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });

  const archiveClient = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('clients')
        .update({ archived: true } as any)
        .eq('id', id)
        .eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });

  const deleteClient = useMutation({
    mutationFn: async (id: string) => {
      const { count: sessionCount } = await (supabase as any)
        .from('sessions')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', id);
      const { count: contractCount } = await (supabase as any)
        .from('signed_contracts')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', id);
      if ((sessionCount ?? 0) > 0 || (contractCount ?? 0) > 0) {
        throw new Error(
          `Ce client a ${sessionCount ?? 0} session(s) et ${contractCount ?? 0} contrat(s) lié(s). Archivez-le plutôt que de le supprimer définitivement.`
        );
      }
      const { error } = await supabase.from('clients').delete().eq('id', id).eq('owner_id', user!.id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });

  return { ...query, clients: query.data || [], createClient, updateClient, archiveClient, deleteClient };
}
